import type {
  PageObjectResponse,
  RichTextItemResponse,
} from '@notionhq/client/build/src/api-endpoints'

export type NotionProperty = PageObjectResponse['properties'][string]
export type NotionPropertyType = NotionProperty['type']

/**
 * Narrows a page property to one property type, returning that type's payload.
 * Notion's own types are a union keyed by `type`, so this is the only way to
 * read a property without a cast at every call site.
 */
export function extractProperty<T extends NotionPropertyType>(
  property: NotionProperty | undefined,
  type: T,
): PropertyValue<T> | undefined {
  if (property?.type !== type) return undefined
  // The union member is pinned by the `type` check above, but TS can't index a
  // union by a generic key — this cast is what the narrowing already proved.
  return (property as Record<T, PropertyValue<T>>)[type]
}

type PropertyValue<T extends NotionPropertyType> =
  Extract<NotionProperty, { type: T }> extends Record<T, infer V> ? V : never

/** Flattens a `title` or `rich_text` property to plain text. */
export function extractPropertyText(property: NotionProperty | undefined) {
  if (!property) return ''
  if (property.type === 'rich_text')
    return richTextToPlainText(property.rich_text)
  if (property.type === 'title') return richTextToPlainText(property.title)
  return ''
}

export function richTextToPlainText(
  richText: Array<Pick<RichTextItemResponse, 'plain_text'>>,
) {
  return richText.map(({ plain_text: plainText }) => plainText).join('')
}
